import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import stringUtils from "../../../_services/string-utils";

const INFO = {
    identifier: "info",
};
const ERROR = {
    identifier: "danger",
};

const WARNING = {
    identifier: "warning",
};

const SUCCESS = {
    identifier: "success",
};

const MessageType = {
    INFO,
    ERROR,
    WARNING,
    SUCCESS,
};

// The prototype leads every band with a glyph matching its tone rather than an
// exclamation mark for anything short of an error - "i" reads as informational even
// on an amber (warning) band.
const TYPE_ICON = {
    [INFO.identifier]: "fa fa-info-circle",
    [WARNING.identifier]: "fa fa-info-circle",
    [ERROR.identifier]: "fa fa-exclamation-circle",
    [SUCCESS.identifier]: "fa fa-check-circle",
};

export const ErrorMessage = (props) => {
    return <MessageComponent type={MessageType.ERROR} {...props} />;
};

export const WarningMessage = (props) => {
    return <MessageComponent type={MessageType.WARNING} {...props} />;
};

export const SuccessMessage = (props) => {
    return <MessageComponent type={MessageType.SUCCESS} {...props} />;
};

export const InfoMessage = (props) => {
    return <MessageComponent type={MessageType.INFO} {...props} />;
};

const MessageComponent = ({
    type,
    children,
    messages,
    style,
    isOpen,
    toggle,
    marginless = false,
    useTopMargin = false,
    useBottomMargin = true,
    "data-cy": dataCy,
    icon,
}) => {
    const messageComponent = determineMessageContent(children, messages);

    if (messageComponent && (!Array.isArray(children) || children.length > 0)) {
        const resolvedIconClass = icon === false ? null : typeof icon === "string" ? icon : TYPE_ICON[type.identifier];
        if (isOpen === false) {
            return <></>;
        }
        return (
            <AlertBoxWrapper
                $color={type.identifier}
                data-cy={dataCy}
                style={{ ...style, ...prepareMarginStyle(marginless, useTopMargin, useBottomMargin) }}
            >
                {resolvedIconClass && <AlertIcon className={resolvedIconClass} aria-hidden="true" />}
                <AlertBody>{messageComponent}</AlertBody>
                {toggle && (
                    <button
                        type="button"
                        className="btn-close"
                        aria-label="Schließen"
                        onClick={toggle}
                    />
                )}
            </AlertBoxWrapper>
        );
    } else {
        return <></>;
    }
};

const MessageContent = ({ message }) => {
    return <div dangerouslySetInnerHTML={{ __html: message }} />;
};

const determineMessageContent = (children, messages) => {
    if (children) {
        if (typeof children === "string" && !stringUtils.isBlank(children)) {
            return <MessageContent message={children} />;
        } else if (Array.isArray(children) && children.length > 0) {
            return children;
        } else {
            return children;
        }
    } else if (!stringUtils.isBlank(messages)) {
        if (typeof messages === "string") {
            return <MessageContent message={messages} />;
        } else if (Array.isArray(messages) && messages.length > 0) {
            return messages;
        } else {
            return messages;
        }
    }
};

const prepareMarginStyle = (marginless, useTopMargin, useBottomMargin) => {
    const style = {
        marginTop: marginless ? "0" : useTopMargin ? "1rem" : "0",
    };
    if (marginless || !useBottomMargin) {
        style.marginBottom = "0";
    }
    return style;
};

const AlertBoxWrapper = styled.div.attrs(({ $color }) => ({
    role: "alert",
    className: `alert alert-${$color}`,
}))`
    display: flex;
    /* flex-start, not center: a band whose body holds more than the message text (e.g.
       action buttons below it) would otherwise center the icon against that whole block
       instead of lining it up with the first line of text. */
    align-items: flex-start;
    gap: var(--gap-m);
    white-space: pre-line;

    & > p {
        margin: 0;
    }
`;

const AlertIcon = styled.i`
    flex: none;
    font-size: 1rem;
    line-height: var(--pw-leading-normal);
`;

const AlertBody = styled.div`
    flex: 1;
    min-width: 0;
`;

const MessagePropTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    style: PropTypes.object,
    marginless: PropTypes.bool,
    useTopMargin: PropTypes.bool,
    useBottomMargin: PropTypes.bool,
    messages: PropTypes.string,
    isOpen: PropTypes.bool,
    toggle: PropTypes.func,
    "data-cy": PropTypes.string,
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
};

MessageComponent.propTypes = {
    type: PropTypes.oneOf([MessageType.INFO, MessageType.ERROR, MessageType.WARNING, MessageType.SUCCESS]).isRequired,
    ...MessagePropTypes,
};

ErrorMessage.propTypes = {
    ...MessagePropTypes,
};

WarningMessage.propTypes = {
    ...MessagePropTypes,
};

SuccessMessage.propTypes = {
    ...MessagePropTypes,
};

InfoMessage.propTypes = {
    ...MessagePropTypes,
};
